/**
 * Every medical file for a clinic's day, on paper.
 *
 * The requirement asks the clinics manager and the doctor's secretary to view
 * or print "all the medical files for a clinic's patients on a given day, or
 * several days". Printing them one at a time is a different thing: a doctor who
 * wants the morning's files in their hand would open, print and close thirty
 * screens, and a stack assembled that way has no way of showing that one file
 * was missed.
 *
 * So the set is fetched as a set — GET /api/patients/files — and rendered as
 * one sheet with a page break between patients. The server decides what each
 * file may contain, including whether prices appear in it; this component only
 * lays it out.
 *
 * The count is stated on the cover page, and if the server capped the range the
 * cover says that too. A print that quietly stopped short is a clinical record
 * with a hole in it that nobody can see.
 */

import { useCallback, useRef, useState } from 'react';
import { Button, DateInput, Field, Icon, Modal, Spinner } from '../../components/common';
import { patientsApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { NOTE_KIND_META } from './MedicalFileView';
import { DEPARTMENT_META, ageFrom, formatDate, printArea } from './labels';
import { formatDmy, ltr } from '../../domain/bidi';

const line = (value) => (value === null || value === undefined || value === '' ? '—' : value);

/** One patient's file, condensed to what is worth carrying on paper. */
function FileSheet({ entry }) {
  const p = entry.patient || {};
  const results = (entry.requests || []).filter((r) => r.status === 'completed' && r.result);
  const diagnoses = (entry.notes || []).filter((n) => n.kind !== 'note');

  return (
    <section style={{ breakAfter: 'page', breakInside: 'avoid-page', marginBottom: 24 }}>
      <header style={{ borderBottom: '1px solid #000', paddingBottom: 6, marginBottom: 10 }}>
        <h2 className="text-base font-bold">{line(p.fullName)}</h2>
        <p className="muted text-xs">
          {entry.appointment?.date ? `${ltr(formatDmy(entry.appointment.date))} ` : ''}
          {entry.appointment?.time ? `${ltr(entry.appointment.time)} — ` : ''}
          {line(entry.appointment?.clinicName)}
          {entry.appointment?.doctorName ? ` — ${entry.appointment.doctorName}` : ''}
        </p>
      </header>

      <table>
        <tbody>
          <tr>
            <th style={{ width: '22%' }}>السن</th>
            <td>{p.dateOfBirth ? ageFrom(p.dateOfBirth) : '—'}</td>
            <th style={{ width: '22%' }}>النوع</th>
            <td>{p.gender === 'male' ? 'ذكر' : p.gender === 'female' ? 'أنثى' : '—'}</td>
          </tr>
          <tr>
            <th>الهاتف</th>
            <td dir="ltr">{line([p.phonePrimary, p.phoneSecondary].filter(Boolean).join(' / '))}</td>
            <th>الرقم القومي</th>
            <td dir="ltr">{line(p.nationalId)}</td>
          </tr>
          <tr>
            <th>التأمين</th>
            <td>{p.patientType === 'insured' ? line(p.insuranceProvider) : 'نقدي'}</td>
            <th>كود التأمين</th>
            <td dir="ltr">{line(p.insuranceCode)}</td>
          </tr>
        </tbody>
      </table>

      {(p.chronicDiseases || []).length > 0 && (
        <p style={{ marginTop: 8 }}>
          <b>أمراض مزمنة: </b>
          {p.chronicDiseases.join('، ')}
        </p>
      )}

      {(p.currentMedications || []).length > 0 && (
        <div style={{ marginTop: 8 }}>
          <b>أدوية حالية</b>
          <table>
            <thead>
              <tr>
                <th>الدواء</th>
                <th style={{ width: '18%' }}>التركيز</th>
                <th style={{ width: '18%' }}>عدد المرات</th>
                <th style={{ width: '20%' }}>منذ</th>
              </tr>
            </thead>
            <tbody>
              {p.currentMedications.map((m, i) => (
                <tr key={i}>
                  <td>{line(m.name)}</td>
                  <td>{line(m.concentration)}</td>
                  <td>{line(m.frequency)}</td>
                  <td>{line(m.duration)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {p.complaints && (
        <p style={{ marginTop: 8 }}>
          <b>الشكوى: </b>
          {p.complaints}
        </p>
      )}

      {diagnoses.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <b>التشخيصات والملاحظات</b>
          <table>
            <tbody>
              {diagnoses.slice(0, 12).map((n) => (
                <tr key={n.id}>
                  <td style={{ width: '20%' }}>{NOTE_KIND_META[n.kind]?.label || n.kind}</td>
                  <td>{n.body}</td>
                  <td style={{ width: '18%' }}>{formatDate(n.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {results.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <b>نتائج</b>
          <table>
            <tbody>
              {results.slice(0, 15).map((r) => (
                <tr key={r.id}>
                  <td style={{ width: '18%' }}>{DEPARTMENT_META[r.department]?.label || r.department}</td>
                  <td>
                    {r.title}
                    {r.result?.summary ? ` — ${r.result.summary}` : ''}
                    {r.result?.impression ? ` — ${r.result.impression}` : ''}
                  </td>
                  <td style={{ width: '18%' }}>{formatDate(r.completedAt || r.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(entry.prescriptions || []).length > 0 && (
        <p style={{ marginTop: 8 }}>
          <b>روشتات: </b>
          {entry.prescriptions.length}
        </p>
      )}
    </section>
  );
}

/**
 * The button, the date range, and the sheet it produces.
 *
 * `clinicId` / `doctorId` narrow the set; the secretary passes neither, because
 * the server already scopes her to the doctor she serves.
 */
export function PrintDayFilesButton({ date, clinicId = null, doctorId = null, institutionName, label = 'طباعة ملفات اليوم' }) {
  const { error } = useNotifications();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [from, setFrom] = useState(date);
  const [to, setTo] = useState(date);
  const sheet = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setData(null);
    try {
      const params = { date: from, to };
      if (clinicId) params.clinicId = clinicId;
      if (doctorId) params.doctorId = doctorId;
      setData(await patientsApi.dayFiles(params));
    } catch (err) {
      error(err.message || 'تعذر تحميل الملفات');
    } finally {
      setLoading(false);
    }
  }, [from, to, clinicId, doctorId, error]);

  // A start moved past the end takes the end with it: a one-day print is the
  // common case, and a reversed range is refused by the server anyway.
  const pickFrom = (value) => {
    setFrom(value);
    if (value && (!to || to < value)) setTo(value);
  };

  const start = () => {
    setFrom(date);
    setTo(date);
    setData(null);
    setOpen(true);
  };

  return (
    <>
      <Button variant="ghost" icon="file-text" onClick={start}>
        {label}
      </Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="طباعة الملفات الطبية"
        icon="file-text"
        size="xl"
        footer={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button variant="ghost" icon="refresh-cw" onClick={load} disabled={loading}>
              تحميل
            </Button>
            <Button icon="printer" onClick={() => printArea(sheet.current)} disabled={!data?.files?.length}>
              طباعة {data?.files?.length ? `(${data.files.length})` : ''}
            </Button>
          </div>
        }
      >
        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="من يوم">
            <DateInput value={from} onChange={(e) => pickFrom(e.target.value)} clearable={false} />
          </Field>
          <Field label="إلى يوم" hint="اتركه كما هو ليوم واحد">
            <DateInput value={to} min={from} onChange={(e) => setTo(e.target.value)} clearable={false} />
          </Field>
        </div>

        {loading && (
          <div className="flex items-center justify-center gap-2 py-10 text-slate-300">
            <Spinner /> جاري تجميع الملفات…
          </div>
        )}

        {!loading && !data && (
          <p className="py-8 text-center text-sm text-slate-400">اختر المدة ثم اضغط تحميل.</p>
        )}

        {!loading && data && data.files.length === 0 && (
          <p className="py-8 text-center text-sm text-slate-400">لا ملفات في هذه المدة.</p>
        )}

        {!loading && data && data.files.length > 0 && (
          <>
            {data.truncated && (
              <p className="mb-3 flex items-center gap-2 rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                <Icon name="alert-triangle" size={14} />
                عدد المرضى {data.total} — تُطبع أول {data.files.length}. قسّم المدة لطباعة الباقي.
              </p>
            )}
            <div ref={sheet} className="print-sheet" dir="rtl">
              <h1 className="text-lg font-bold">
                {institutionName || 'المنشأة'} — الملفات الطبية{' '}
                {data.from === data.to
                  ? `ليوم ${ltr(formatDmy(data.from))}`
                  : `من ${ltr(formatDmy(data.from))} إلى ${ltr(formatDmy(data.to))}`}
              </h1>
              <p className="muted text-xs" style={{ marginBottom: 12 }}>
                {data.files.length} ملف{data.truncated ? ` من ${data.total}` : ''}
              </p>
              {data.files.map((entry) => (
                <FileSheet key={entry.patient.id} entry={entry} />
              ))}
            </div>
          </>
        )}
      </Modal>
    </>
  );
}
